/**
 * Pagination — the design system's capsule pager.
 *
 * Ported from:
 * resources/Dental supply storefront design system/tw-components.jsx
 *
 * Deviations from upstream are mechanical:
 * • `cx` and `Icon` are imported rather than defined in the upstream bundle.
 * • Shared keyframes are loaded from the vendored keyframe module.
 * • Tailwind CDN retry timers are omitted because the app ships compiled CSS.
 *
 * One behavioural addition, because the reference is a desktop-only prototype: the page
 * window narrows below 480px, and the row wraps.
 *
 * At upstream's 7 slots the capsule is ~440px (7 x `min-w-[56px]` + gaps + padding) and
 * the whole `nav` measured **668px**, which overflowed a 390px viewport by 139px and
 * shifted the entire listing page sideways. The window narrows rather than the slots so
 * the buttons keep a 56px touch target.
 *
 * The narrow window is **exactly 3 slots**, sized from measurement rather than arithmetic:
 * at 390px the row has 350px to work with, and the children measured prev 44 + capsule 256
 * (4 slots) + next 44 + caption 90 with a 16px gap — so even 4 slots put row one at 376px
 * and `flex-wrap` broke it into four ragged lines with the arrows stranded on their own.
 * Three slots make the capsule 192px and row one 312px, leaving "Page X of Y" to wrap onto
 * a tidy second line.
 *
 * It renders `[1, …, total]` on the first and last page and `[1, page, total]` in between —
 * **always including the current page**, because `measureIndicator` keys off
 * `[aria-current="page"]`. Above 480px the arithmetic is byte-identical to upstream's.
 */
import React from "react";

import { cx } from "./cx";
import { Icon } from "./Icon";
import "./keyframes";

export function Pagination({
  page = 1,
  total = 1,
  onChange,
  loading = false,
  style = {},
  className = "",
}) {
  const go = (nextPage) => {
    const clampedPage = Math.min(total, Math.max(1, nextPage));
    if (clampedPage !== page) {
      setBusy(true);
      onChange?.(clampedPage);
    }
  };
  const [indicator, setIndicator] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [hoveredEllipsis, setHoveredEllipsis] = React.useState(-1);
  const capsuleRef = React.useRef(null);

  React.useEffect(() => {
    setBusy(false);
  }, [page]);

  React.useEffect(() => {
    if (!busy) return undefined;

    const timeout = setTimeout(() => setBusy(false), 1500);
    return () => clearTimeout(timeout);
  }, [busy]);

  // 7 slots is upstream's window; 5 is the narrow-viewport variant (see the note above).
  const [wide, setWide] = React.useState(true);

  React.useEffect(() => {
    const query = window.matchMedia("(min-width: 480px)");
    const apply = () => setWide(query.matches);
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, []);

  const pages = wide
    ? total <= 7
      ? Array.from({ length: total }, (_, index) => index + 1)
      : page <= 4
        ? [1, 2, 3, 4, 5, "\u2026", total]
        : page >= total - 3
          ? [1, "\u2026", total - 4, total - 3, total - 2, total - 1, total]
          : [1, "\u2026", page - 1, page, page + 1, "\u2026", total]
    : total <= 3
      ? Array.from({ length: total }, (_, index) => index + 1)
      : page === 1 || page === total
        ? [1, "\u2026", total]
        : [1, page, total];

  const measureIndicator = React.useCallback(() => {
    const capsule = capsuleRef.current;
    if (!capsule) return;

    // Clear rather than bail: bailing leaves the blue pill parked under whichever slot it
    // measured last, so a window that no longer contains the current page would highlight
    // the wrong number. The narrow window below always includes it, but this keeps any
    // future window honest.
    const activeButton = capsule.querySelector('[aria-current="page"]');
    if (!activeButton) {
      setIndicator(null);
      return;
    }

    const x = activeButton.offsetLeft;
    const width = activeButton.offsetWidth;

    // Keep the aligned fallback fill until utility styles have supplied the
    // documented 56px minimum width.
    if (width < 48) {
      setIndicator(null);
      return;
    }

    setIndicator((previous) =>
      previous && previous.x === x && previous.width === width
        ? previous
        : { x, width }
    );
  }, []);

  React.useLayoutEffect(() => {
    measureIndicator();

    let mounted = true;
    let resizeObserver = null;

    if (window.ResizeObserver && capsuleRef.current) {
      resizeObserver = new ResizeObserver(measureIndicator);
      resizeObserver.observe(capsuleRef.current);
      Array.from(capsuleRef.current.children).forEach((child) =>
        resizeObserver.observe(child)
      );
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (mounted) measureIndicator();
      });
    }

    const animationFrame = requestAnimationFrame(measureIndicator);
    window.addEventListener("resize", measureIndicator);

    return () => {
      mounted = false;
      resizeObserver?.disconnect();
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", measureIndicator);
    };
  }, [page, total, measureIndicator]);

  const navigationButtonClassName = cx(
    "w-[44px] h-[44px] rounded-full border-[1.5px] border-line bg-card text-ink-2 cursor-pointer inline-flex items-center justify-center shrink-0",
    "[transition:transform_var(--duration-base)_var(--ease-spring),box-shadow_var(--duration-base)_var(--ease-out),background_var(--duration-fast),border-color_var(--duration-fast),color_var(--duration-fast)]",
    "enabled:hover:bg-blue-600 enabled:hover:border-blue-600 enabled:hover:text-white enabled:hover:shadow-ocean enabled:active:scale-[0.92]",
    "disabled:opacity-[0.45] disabled:cursor-not-allowed focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
  );

  const numberButtonClassName = (active) =>
    cx(
      "dnd-pg__btn tabular-nums relative z-[1] min-w-[56px] h-[40px] px-[14px] border-0 rounded-pill cursor-pointer font-sans text-sm font-semibold inline-flex items-center justify-center bg-transparent",
      "[transition:color_var(--duration-base)_var(--ease-standard),background_var(--duration-fast),transform_var(--duration-base)_var(--ease-spring)]",
      "enabled:active:scale-90 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]",
      active
        ? cx("text-white", !indicator && "bg-blue-600 shadow-ocean")
        : "text-ink-2 enabled:hover:text-ink enabled:hover:bg-neutral-100"
    );

  const renderEllipsis = (index) => {
    const jumpsBack = index === 1 && page > 4;
    const directionLabel = jumpsBack ? "Back" : "Ahead";

    return (
      <button
        key={`ellipsis-${index}`}
        type="button"
        className="dnd-pg__dots relative z-[1] min-w-[56px] h-[40px] border-0 rounded-pill cursor-pointer bg-transparent text-ink-3 inline-flex items-center justify-center font-sans text-sm [transition:color_var(--duration-fast),background_var(--duration-fast)] hover:text-blue-700 hover:bg-blue-50 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
        onMouseEnter={() => setHoveredEllipsis(index)}
        onMouseLeave={() => setHoveredEllipsis(-1)}
        onFocus={() => setHoveredEllipsis(index)}
        onBlur={() => setHoveredEllipsis(-1)}
        onClick={() => go(page + (jumpsBack ? -10 : 10))}
        aria-label={`Jump ${directionLabel.toLowerCase()} 10 pages`}
        title={`${directionLabel} 10 pages`}
      >
        {hoveredEllipsis === index ? (
          <span
            key="jump"
            className="inline-flex animate-[dnd-fade_var(--duration-base)_var(--ease-out)]"
          >
            <Icon
              name={
                jumpsBack
                  ? "keyboard_double_arrow_left"
                  : "keyboard_double_arrow_right"
              }
              size={18}
              weight={500}
            />
          </span>
        ) : (
          <span
            key="dots"
            className="animate-[dnd-fade_var(--duration-base)_var(--ease-out)]"
          >
            {"\u2026"}
          </span>
        )}
      </button>
    );
  };

  const isBusy = busy || loading;

  return (
    <nav
      className={cx(
        "dnd-pg inline-flex flex-col items-center gap-[var(--space-3)] font-sans [transition:opacity_var(--duration-fast)]",
        className
      )}
      aria-label="Pagination"
      aria-busy={isBusy || undefined}
      style={{
        opacity: isBusy ? 0.6 : 1,
        pointerEvents: isBusy ? "none" : "auto",
        ...style,
      }}
    >
      <div className="flex max-w-full flex-wrap items-center justify-center gap-[var(--space-4)]">
        <button
          type="button"
          className={navigationButtonClassName}
          disabled={page <= 1}
          onClick={() => go(page - 1)}
          aria-label="Previous page"
        >
          <Icon name="chevron_left" size={20} weight={500} />
        </button>

        <div
          ref={capsuleRef}
          className="relative inline-flex items-center gap-[6px] p-[6px] bg-card border border-line-subtle rounded-pill shadow-sm"
        >
          {indicator && (
            <span
              className="dnd-pg__ind absolute top-[10px] left-0 h-[32px] rounded-pill bg-blue-600 shadow-ocean [transition:transform_var(--duration-base)_var(--ease-spring),width_var(--duration-base)_var(--ease-out)]"
              style={{
                width: `${Math.max(32, indicator.width - 12)}px`,
                transform: `translateX(${
                  indicator.x + Math.min(6, (indicator.width - 32) / 2)
                }px)`,
              }}
              aria-hidden="true"
            />
          )}

          {pages.map((pageNumber, index) =>
            pageNumber === "\u2026" ? (
              renderEllipsis(index)
            ) : (
              <button
                key={pageNumber}
                type="button"
                className={numberButtonClassName(pageNumber === page)}
                aria-current={pageNumber === page ? "page" : undefined}
                onClick={() => go(pageNumber)}
              >
                {pageNumber.toLocaleString()}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          className={navigationButtonClassName}
          disabled={page >= total}
          onClick={() => go(page + 1)}
          aria-label="Next page"
        >
          <Icon name="chevron_right" size={20} weight={500} />
        </button>

        <span className="font-sans text-sm text-ink-3 whitespace-nowrap pl-[var(--space-1)] tabular-nums">
          <span
            key={page}
            className="animate-[dnd-fade_var(--duration-base)_var(--ease-out)]"
          >
            Page {page.toLocaleString()} of {total.toLocaleString()}
          </span>
        </span>
      </div>
    </nav>
  );
}
